// Mirrors pi-poc/src/protocol.ts (Cadence spec #132); keep the two in sync

export const MAX_TEXT_CHARS = 20_000;

export type ThinkingLevel =
  | 'off'
  | 'minimal'
  | 'low'
  | 'medium'
  | 'high'
  | 'xhigh'
  | 'max';
export interface ModelRef {
  provider: string;
  id: string;
}
export interface ModelInfo extends ModelRef {
  name: string;
  thinkingLevels: ThinkingLevel[];
}
export interface SessionContext {
  targetType?: 'dashboard' | 'homepage' | 'chart_group';
  dashboardId?: string;
  dashletId?: string;
  objectId?: string;
}
export interface Usage {
  input: number;
  output: number;
  cacheRead: number;
  cacheWrite: number;
  totalTokens: number;
  costTotal: number;
}
export interface SourceFile {
  path: string;
  content: string;
}
export interface SnapshotRef {
  id: string;
  createdAt: string; // ISO 8601
  reason: 'push';
  dashletId: string | null;
  filesUrl?: string; // presigned GET of the Snapshot JSON; absolute or relative to the ws URL's http(s) origin
}
export interface Snapshot extends Omit<SnapshotRef, 'filesUrl'> {
  files: SourceFile[];
}
export interface StoredCapture {
  path: string;
  width: number;
  height: number;
}
export type HistoryItem =
  | { kind: 'user'; text: string }
  | { kind: 'assistant'; text: string }
  | {
      kind: 'tool';
      toolName: string;
      isError: boolean;
      summary: string;
      toolCallId?: string;
      capture?: StoredCapture;
    };
export interface PushInfo {
  action: 'created' | 'updated';
  dashboardId: string;
  dashletId: string;
  url: string;
}
export interface SessionCredentials {
  apiKey: string;
  userId: string;
  businessId: string;
  expires: string | number;
}
export type ClientCapability = 'capture';
export type CaptureFailureCode =
  | 'not_rendered'
  | 'timeout'
  | 'capture_failed'
  | 'unsupported'
  | 'too_large';
export interface CaptureUploadTarget {
  url: string;
  method: 'PUT';
  headers: Record<string, string>;
  maxBytes: number;
}
export interface CaptureUpload {
  png: CaptureUploadTarget;
  failure: CaptureUploadTarget;
  expiresAt: number; // unix ms
}
export interface CaptureFailureBody {
  code: CaptureFailureCode;
  message?: string;
}
export type ClientMessage =
  | {
      type: 'session.start';
      context: SessionContext;
      model?: ModelRef;
      thinkingLevel?: ThinkingLevel;
      capabilities?: ClientCapability[];
      conversationId?: string;
      credentials: SessionCredentials;
      profile?: string;
    }
  | {
      type: 'snapshot.restore';
      snapshotId: string;
      credentials?: SessionCredentials;
    }
  | {
      type: 'user.message';
      text: string;
      credentials: SessionCredentials;
      clientMessageId: string;
    }
  | { type: 'session.abort' };
export type ServerMessage =
  | {
      type: 'session.ready';
      sessionId: string;
      models: ModelInfo[];
      model: ModelRef;
      thinkingLevel: ThinkingLevel;
      context: SessionContext;
      limits: { maxMs: number; idleMs: number };
      conversationId: string;
      resumed: boolean;
      turnActive: boolean;
      history: HistoryItem[];
      snapshots?: SnapshotRef[];
      snapshotPointer?: number;
    }
  | { type: 'assistant.delta'; kind: 'text' | 'thinking'; delta: string }
  | {
      type: 'assistant.message';
      text: string;
      usage?: Usage;
      stopReason: string;
    }
  | {
      type: 'tool.generating';
      toolCallId: string;
      toolName: string;
      argsSummary?: string;
      chars: number;
    }
  | { type: 'tool.start'; toolCallId: string; toolName: string; args: unknown }
  | { type: 'tool.update'; toolCallId: string; partial: string }
  | {
      type: 'tool.end';
      toolCallId: string;
      toolName: string;
      isError: boolean;
      summary: string;
      capture?: StoredCapture;
    }
  | { type: 'turn.settled' }
  | {
      type: 'push.result';
      action: 'created' | 'updated';
      dashboardId: string;
      dashletId: string;
      url: string;
      conversationId: string;
      snapshot: SnapshotRef;
    }
  | {
      type: 'snapshot.restored';
      snapshotId: string;
      push: PushInfo;
      filesUrl: string;
    }
  | ErrorFrame
  | { type: 'message.accepted'; clientMessageId: string }
  | {
      type: 'block.capture';
      requestId: string;
      dashletId: string;
      toolCallId?: string;
      upload: CaptureUpload;
    }
  | { type: 'session.closed'; reason: SessionClosedReason };

export const SESSION_CLOSED_REASONS = [
  'client',
  'timeout_max',
  'timeout_idle',
  'auth',
  'server_error',
  'capacity',
  'conflict',
  'not_found',
  'forbidden',
] as const;
export type SessionClosedReason = (typeof SESSION_CLOSED_REASONS)[number];

export const ERROR_CODES = [
  'bad_message',
  'not_ready',
  'already_started',
  'invalid_context',
  'bad_request',
  'auth',
  'not_found',
  'forbidden',
  'conflict',
  'session_create_failed',
  'conversation_invalid',
  'model_unavailable',
  'busy',
  'restore_busy',
  'state_unavailable',
  'closed',
  'cancelled',
  'persist_failed',
  'files_skipped',
  'prompt_failed',
  'abort_failed',
  'unsupported',
  'unknown_snapshot',
  'restore_failed',
  'restore_note_failed',
] as const;
export type ErrorCode = (typeof ERROR_CODES)[number];

export const AUTH_REASONS = [
  'missing',
  'expired',
  'rejected',
  'unverified',
] as const;
export const BAD_REQUEST_REASONS = [
  'unknown_profile',
  'missing_required_pack',
  'invalid_conversation_id',
  'context_rejected',
  'profile_mismatch',
] as const;
export const BUSY_REASONS = ['conversation_locked', 'save_pending'] as const;
export const STATE_UNAVAILABLE_REASONS = [
  'lock_unavailable',
  'save_pending',
  'head_check_failed',
  'lease_lost',
] as const;
export const PERSIST_FAILURES = ['turn', 'version', 'timeout'] as const;
export const RESTORE_FAILURES = [
  'no_dashlet',
  'workspace',
  'push',
  'internal',
] as const;
export const SKIPPED_FILE_REASONS = [
  'too_large',
  'not_utf8',
  'too_many',
] as const;
export const CONTEXT_ID_FIELDS = [
  'dashboardId',
  'dashletId',
  'objectId',
] as const;

export type AuthReason = (typeof AUTH_REASONS)[number];
export type BadRequestReason = (typeof BAD_REQUEST_REASONS)[number];
export type BusyReason = (typeof BUSY_REASONS)[number];
export type StateUnavailableReason = (typeof STATE_UNAVAILABLE_REASONS)[number];
export type PersistFailure = (typeof PERSIST_FAILURES)[number];
export type RestoreFailure = (typeof RESTORE_FAILURES)[number];
export type SkippedFileReason = (typeof SKIPPED_FILE_REASONS)[number];
export type ContextIdField = (typeof CONTEXT_ID_FIELDS)[number];

export interface ErrorParams {
  auth: { reason: AuthReason };
  bad_request: { reason: BadRequestReason };
  invalid_context: { fields: ContextIdField[] };
  busy: { reason: BusyReason };
  state_unavailable: { reason: StateUnavailableReason };
  persist_failed: { what: PersistFailure };
  files_skipped: {
    files: { path: string; reason: SkippedFileReason }[];
    more: number;
  };
  restore_failed: { reason: RestoreFailure };
}
export type ErrorCodeWithParams = keyof ErrorParams;
type ErrorFrameOf<C extends ErrorCode> = {
  type: 'error';
  code: C;
  clientMessageId?: string;
} & (C extends ErrorCodeWithParams
  ? { params: ErrorParams[C] }
  : { params?: never });
export type ErrorFrame = { [C in ErrorCode]: ErrorFrameOf<C> }[ErrorCode];
